import { Link } from '@tanstack/react-router'
import { GraduationCap, PlayCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ProgressRing } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useEnrollments } from '@/features/learning/api'
import { formatDate, formatDuration, formatRelative } from '@/lib/format'

export function MyCoursesPage() {
  const { data, isLoading } = useEnrollments()
  const enrollments = data?.enrollments ?? []

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Kelas saya</h1>
        <p className="mt-1 text-muted">Lanjutkan dari tempat Anda berhenti terakhir kali.</p>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 rounded-2xl" />
          ))}
        </div>
      ) : enrollments.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<GraduationCap className="size-6" />}
            title="Belum ada kelas"
            description="Kelas yang Anda beli atau ikuti gratis akan muncul di sini."
            action={
              <Link to="/kelas">
                <Button>Jelajahi katalog</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {enrollments.map((enrollment) => (
            <Card key={enrollment.id}>
              <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {enrollment.course.thumbnail_url ? (
                  <img
                    src={enrollment.course.thumbnail_url}
                    alt=""
                    className="h-28 w-full shrink-0 rounded-xl object-cover sm:h-20 sm:w-32"
                  />
                ) : null}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="brand">{enrollment.course.category}</Badge>
                    {enrollment.status === 'completed' ? <Badge tone="success">Selesai</Badge> : null}
                  </div>
                  <h2 className="mt-1.5 font-bold text-ink">{enrollment.course.title}</h2>
                  <p className="mt-0.5 text-sm text-muted">
                    {enrollment.course.expert_name} · {formatDuration(enrollment.course.duration_minutes)}
                  </p>
                  <p className="mt-1 text-xs text-subtle">
                    Bergabung {formatDate(enrollment.enrolled_at)}
                    {enrollment.last_seen_at ? ` · terakhir dibuka ${formatRelative(enrollment.last_seen_at)}` : ''}
                  </p>
                </div>

                <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                  <ProgressRing value={enrollment.progress_percent} />
                  <Link to="/app/belajar/$enrollmentId" params={{ enrollmentId: enrollment.id }}>
                    <Button size="sm">
                      <PlayCircle className="size-4" />
                      {enrollment.progress_percent > 0 ? 'Lanjutkan' : 'Mulai'}
                    </Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
